import { AsyncLocalStorage } from 'async_hooks';

/**
 * Request-scoped site identity for logging.
 *
 * One App Service serves ~25 sites, so every log line needs to say which site it
 * belongs to. Passing a site down to the ~100 logger calls (most of them deep in
 * the mappers) is not practical, so we carry it in an AsyncLocalStorage store
 * that is entered once per request and read by the Winston format in
 * ./loggerWinston.
 *
 * AsyncLocalStorage - not a module-level variable - because the server handles
 * many requests concurrently in one process: a shared variable would be
 * overwritten by another site's request across an `await`, producing logs
 * labelled with the wrong site, which is worse than unlabelled ones.
 */
export interface SiteLogContext {
	siteHost?: string;
	locale?: string;
}

const isServer = typeof window === 'undefined';

// AsyncLocalStorage is server-only. `async_hooks` is mapped to an empty module
// for the client bundle in next.config.js, so this must never be constructed
// in the browser.
const storage = isServer ? new AsyncLocalStorage<SiteLogContext>() : null;

// The browser has no AsyncLocalStorage, but it only ever shows one site per tab,
// so a module-level value is safe there. Seeded from pageProps in _app.tsx.
let browserContext: SiteLogContext = {};

/**
 * Hosts reach us in inconsistent shapes - getPhysicalFilePath() strips the
 * protocol and port before base64-encoding, while getRequestHost() keeps the
 * port. Normalising here stops one site from showing up as several rows in
 * Application Insights.
 *
 * This affects the logged value only. Site resolution still uses its own
 * exact-match comparison in getSiteConfigFromCDA().
 */
export const normalizeHost = (host?: string): string | undefined =>
	host
		?.replace(/^https?:\/\//, '')
		.replace(/:\d+$/, '')
		.toLowerCase() || undefined;

const normalize = (context: SiteLogContext): SiteLogContext => ({
	...context,
	siteHost: normalizeHost(context.siteHost),
});

/**
 * Runs `fn` with the given site attached to every log call made beneath it,
 * however deep and across any number of awaits.
 */
export const runWithSiteContext = <T>(context: SiteLogContext, fn: () => T): T => (storage ? storage.run(normalize(context), fn) : fn());

/**
 * Browser only. The server must never use this - it would be shared across
 * concurrent requests.
 */
export const setBrowserSiteContext = (context: SiteLogContext): void => {
	if (!isServer) browserContext = { ...browserContext, ...normalize(context) };
};

/**
 * Returns undefined on the server when called outside a runWithSiteContext()
 * scope, so logs are never attributed to the wrong site.
 */
export const getSiteContext = (): SiteLogContext | undefined => (isServer ? storage?.getStore() : browserContext);
